import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../../Features/User/UserSlice';
import './RegisterUserMedical.css';
import locationIcon from '../../Assets/Users/location.svg';
import { useLocation } from 'react-router-dom';

const RegisterUserMedical = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('user');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationStatus, setLocationStatus] = useState('');
  const [image, setImage] = useState(null);
  const [MobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const [redirectTimeout, setRedirectTimeout] = useState(null);
  const [isReferralFromQR, setIsReferralFromQR] = useState(false);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [manualReferralCode, setManualReferralCode] = useState('');

  const location = useLocation();

  // Extract referral code from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      setReferralCode(refCode);
      setIsReferralFromQR(true);
      setUserType('user'); // Force user type to 'user' when coming from QR
      setReferralMessage(`🎉 Welcome! You're registering as a customer with referral code: ${refCode}`);
      console.log('Referral code detected from URL:', refCode);
    }
  }, [location]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [redirectTimeout]);

  const getLocation = () => {
    setLocationStatus('Fetching your location...');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setLocationStatus('Location added successfully!');
        },
        () => {
          setLocationStatus('Error: Unable to fetch location. Please allow location access.');
        }
      );
    } else {
      setLocationStatus('Error: Geolocation is not supported by this browser.');
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('password', password);
    formData.append('role', userType === 'swasthmitra' ? 'employee' : 'user');
    formData.append('latitude', latitude);
    formData.append('longitude', longitude);
    formData.append('MobileNumber', MobileNumber);

    // Include referral code if present (either from QR or manual entry)
    const finalReferralCode = referralCode || manualReferralCode;
    if (finalReferralCode) {
      formData.append('referralCode', finalReferralCode);
      console.log('Including referral code in registration:', finalReferralCode);
    }

    if (image) {
      formData.append('image', image);
    }

    try {
      const response = await axios.post(
        'https://api.swasthmitra.com/api/usermedical/register',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setIsLoading(false);
      
      // Auto-login: Store token and user data
      if (response.data.token && response.data.user) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Set user in Redux store
        dispatch(setUser(response.data.user));
        
        // Show success popup briefly, then redirect
        setShowPopup(true);
        
        // Auto-redirect to home after 2 seconds
        const timeoutId = setTimeout(() => {
          setShowPopup(false);
          navigate('/home');
        }, 2000);
        setRedirectTimeout(timeoutId);
      } else {
        // Fallback: just show popup for approval pending cases
        setShowPopup(true);
      }

      // Reset form
      setName('');
      setEmail('');
      setPassword('');
      setUserType('user');
      setLatitude('');
      setLongitude('');
      setLocationStatus('');
      setImage(null);
      setMobileNumber('');
      if (!isReferralFromQR) {
        setManualReferralCode('');
        setShowReferralInput(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Registration error:', error);
      alert(error.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <div className="registration-container">
      
      <div className="registration-form-container">
        {referralMessage && (
          <div className="referral-notice">
           
            <div className="referral-text">
              <p>{referralMessage}</p>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name*</label>
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Enter your full name"
                required 
              />
            </div>
            
            <div className="form-group">
              <label>Email Address*</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="Enter your email"
                required 
              />
            </div>
            
            <div className="form-group">
              <label>Password*</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="••••••••"
                required 
              />
            </div>
            
            <div className="form-group">
              <label>Mobile Number*</label>
              <input 
                type="text" 
                value={MobileNumber} 
                onChange={(e) => setMobileNumber(e.target.value)} 
                placeholder="Enter your mobile number"
                required 
              />
            </div>

            <div className="form-group">
              <label>Register As</label>
              <select 
                value={userType} 
                onChange={(e) => setUserType(e.target.value)} 
                required
                disabled={isReferralFromQR} // Disable when coming from QR
                className={isReferralFromQR ? 'disabled-select' : ''}
              >
                <option value="user">User only</option>
                <option value="swasthmitra">Swasthmitra</option>
              </select>
              {isReferralFromQR && (
                <small className="qr-registration-note">
                  QR code registrations are for customers only
                </small>
              )}
            </div>

            {/* Referral Code Section */}
            {isReferralFromQR ? (
              <div className="form-group">
                <label>Referral Code (from QR)</label>
                <input 
                  type="text" 
                  value={referralCode}
                  readOnly
                  className="referral-input-readonly"
                  placeholder="Referral code from QR scan"
                />
                <small className="referral-note">This code was automatically filled from your QR scan</small>
              </div>
            ) : (
              <div className="form-group referral-section">
                <div className="referral-checkbox-container">
                  <input 
                    type="checkbox" 
                    id="has-referral" 
                    checked={showReferralInput}
                    onChange={(e) => {
                      setShowReferralInput(e.target.checked);
                      if (!e.target.checked) {
                        setManualReferralCode('');
                      }
                    }}
                  />
                  <label htmlFor="has-referral" className="checkbox-label">
                    I have a referral code
                  </label>
                </div>
                {showReferralInput && (
                  <div className="referral-input-container">
                    <input 
                      type="text" 
                      value={manualReferralCode}
                      onChange={(e) => setManualReferralCode(e.target.value.trim())}
                      placeholder="Enter your referral code"
                      className="referral-input"
                    />
                    <small className="referral-note">Enter the referral code you received</small>
                  </div>
                )}
              </div>
            )}



            <div className="form-group full-width">
              <div className="location-section">
                <button type="button" className="location-btn" onClick={getLocation}>
                  <img src={locationIcon} alt="Location" className="location-icon" />
                  Add My Location*
                </button>
                <div className={`location-status ${locationStatus.includes('Error') ? 'error' : 'success'}`}>
                  {locationStatus}
                </div>
                {latitude && longitude && (
                  <div className="location-coordinates">
                    <span>Latitude: {latitude.toFixed(4)}</span>
                    <span>Longitude: {longitude.toFixed(4)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group full-width">
              <label>Profile Picture (Optional) </label>
              <div className="file-upload-wrapper">
                <input 
                  type="file" 
                  id="profile-upload" 
                  onChange={(e) => setImage(e.target.files[0])} 
                  accept="image/*" 
                />
                <label htmlFor="profile-upload" className="file-upload-label">
                  {image ? (
                    <span className="file-name">{image.name}</span>
                  ) : (
                    <>
                      <span className="upload-icon1">+</span>
                      <span>Upload Image</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
      </div>

      {showPopup && (
        <div className="registration-popup">
          <div className="popup-content1">
            <div className="popup-icon">
              <svg viewBox="0 0 24 24">
                <path fill="#ff6600" d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" />
              </svg>
            </div>
            <h3>Registration Successful!</h3>
            <p>
              {userType === 'user' 
                ? "Welcome! You're being logged in automatically..." 
                : "Registration submitted! You're being logged in automatically..."}
            </p>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              marginTop: '15px',
              fontSize: '14px',
              color: '#666'
            }}>
              <span className="spinner" style={{ marginRight: '8px' }}></span>
              Redirecting to your dashboard...
            </div>
            <button 
              onClick={() => {
                if (redirectTimeout) {
                  clearTimeout(redirectTimeout);
                }
                setShowPopup(false);
                navigate('/home');
              }}
              className="popup-close-btn"
              style={{ marginTop: '15px' }}
            >
              Continue Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterUserMedical;